import * as React from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import i18n from 'modules/core/lib/i18n'

import type AutomaticRuleForm from 'modules/attendance/components/SettingItemPage/TimeTracking/BreakSettings/AutomaticBreakSettings/AutomaticRules/form'
import { TimeSettingsBreakConfigs } from 'modules/attendance/hooks/useFetchTimeSettingsBreakConfigurations'
import { MAX_BREAKS_LENGTH_RULES } from 'modules/attendance/lib/utils/BreakSettings'

import RuleItem from './RuleItem'
import type RuleForm from './form'

interface Props {
  automaticRuleForms: AutomaticRuleForm[]
  addAutomaticRule: () => void
  deleteAutomaticRule: (form: AutomaticRuleForm) => void
  timeSettingsBreakConfigurations: TimeSettingsBreakConfigs[]
  attendanceSettingId: number
}

const AutomaticRules = ({
  automaticRuleForms,
  addAutomaticRule,
  deleteAutomaticRule,
  timeSettingsBreakConfigurations,
  attendanceSettingId,
}: Props) => {
  const maxRulesSize = MAX_BREAKS_LENGTH_RULES

  const validRules = automaticRuleForms.filter((form) => !form.get('_destroy').value)
  const rulesSize = validRules.length

  const canAdd = (index: number) => {
    const isLast = index + 1 === rulesSize

    return isLast && rulesSize < maxRulesSize
  }

  const ruleItemLabels = [
    i18n.t('time_tracking.settings.breaks.instance.automatic_rules.rule_item_labels.1st'),
    i18n.t('time_tracking.settings.breaks.instance.automatic_rules.rule_item_labels.2nd'),
    i18n.t('time_tracking.settings.breaks.instance.automatic_rules.rule_item_labels.3rd'),
  ]

  const handleConfigurationChange = (form: RuleForm, newValue: number) => {
    form.get('timeSettingsBreakConfigurationId').set(newValue)
  }
  const crypto = window.crypto || window.msCrypto
  const randomIds = new Uint32Array(1)
  const [randomId, setRandomId] = React.useState(crypto.getRandomValues(randomIds))

  const addDurationRule = () => {
    addAutomaticRule()
    setRandomId(crypto.getRandomValues(randomIds))
  }

  const deleteDurationRule = (form: AutomaticRuleForm) => {
    deleteAutomaticRule(form)
    setRandomId(crypto.getRandomValues(randomIds))
  }

  return (
    <Box flexDirection="column">
      <Box paddingBottom="s12" gap="s4">
        <Text.Normal weight="medium" color="grey1000">
          {i18n.t('time_tracking.settings.breaks.instance.automatic_rules.title')}
        </Text.Normal>
        <Text.Normal>
          {i18n.t('time_tracking.settings.breaks.instance.automatic_rules.description')}
        </Text.Normal>
      </Box>
      <Box gap="s16">
        {validRules.map((form: RuleForm, index: number) => (
          <RuleItem
            // Most forms don't have an id
            // so we use randomId instead to prevent React from reusing the same component
            key={`${index}_${randomId}`}
            form={form}
            onAdd={addDurationRule}
            onDelete={deleteDurationRule}
            label={ruleItemLabels[index] || ''}
            canAdd={canAdd(index)}
            rulesSize={rulesSize}
            timeSettingsBreakConfigurations={timeSettingsBreakConfigurations}
            selectedBreakType={Number(form.values.timeSettingsBreakConfigurationId)}
            onChange={(newValue) => handleConfigurationChange(form, newValue)}
            attendanceSettingId={attendanceSettingId}
          />
        ))}
      </Box>
    </Box>
  )
}

export default observer(AutomaticRules)
